import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Container,
  Button,
  CircularProgress,
  Card,
  CardHeader,
  CardActions,
  IconButton,
  CardContent,
  Dialog,
  DialogContent,
  Snackbar,
  DialogTitle,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BookingCalendar from '../../components/customer/BookingCalendar';
import { WorkingHours, BlockedTime } from '../../types/expert';
import { Meeting } from '../../types/meetings';
import {
  InsertLink,
  HelpOutline,
  Close as CloseIcon,
} from '@mui/icons-material';
import { ReactComponent as LinkedinIcon } from '../../assets/linkedin.svg';
import { ReactComponent as InstagramIcon } from '../../assets/instagram.svg';
import { ReactComponent as FacebookIcon } from '../../assets/facebook.svg';
import { ReactComponent as CalendarIcon } from '../../assets/calendar.svg';
import { ReactComponent as ListIcon } from '../../assets/list.svg';
import { ReactComponent as SendIcon } from '../../assets/send.svg';
import { ReactComponent as VideoIcon } from '../../assets/video.svg';
import { ReactComponent as PhoneIcon } from '../../assets/phone.svg';
import { ReactComponent as Link2Icon } from '../../assets/link-2.svg';
import { ReactComponent as ShareIcon } from '../../assets/share.svg';
import { ReactComponent as QRCodeIcon } from '../../assets/qrcode.svg';
import { QRCodeSVG } from 'qrcode.react';
import { getPubExpertProfile } from '../../utils/triplinkService';
import { useBusiness } from '../../redux/slices/business/useBusiness';
import TripQuestionnaire from '../../components/customer/TripQuestionnaire';

interface ExpertData {
  name: string;
  preferred_link: string;
  title: string;
  imageUrl: string;
  description: string;
  timezone: string;
  location: string;
  brandingColor: string;
  businessName: string;
  businessDomain: string;
  workingHours: WorkingHours[];
  blockedTimes: BlockedTime[];
  existingMeetings: Meeting[];
  socialLinks: {
    instagram: string;
    facebook: string;
    linkedin: string;
  };
}

interface PlanPageProps {
  isPreview?: boolean;
  expertUrl?: string;
}

const PageContainer = styled(Container)(({ theme }) => ({
  padding: 0,
  maxWidth: '100%',
  display: 'flex',
  justifyContent: 'center',

  [theme.breakpoints.up('sm')]: {
    padding: '32px',
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '32px',
  maxWidth: '600px',
  width: '100%'
}));

const ExpertCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 350px;
  border-radius: 0;
`
const BottomSheet = styled(CardActions)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  [theme.breakpoints.down('md')]: {
    position: "fixed",
    bottom: 0,
  }
}));

const ProfileImage = styled(Avatar)(({ theme }) => ({
  width: '160px',
  height: '160px',
  borderRadius: '80px',
  objectFit: 'cover',

  [theme.breakpoints.up('md')]: {
    margin: 0,
  },
}));

const ExpertInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  padding: '10px',
  [theme.breakpoints.up('sm')]: {
    padding: '10px 40px'
  }
}));

const SocialLinksWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-around',
  margin: '20px 0'
}))

const ActionButton = styled(Button)(({ theme }) => ({
  padding: '6px 24px',
  margin: '8px 0',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 400,
  width: '100%',
}));

const CalendarWrapper = styled(Box)({
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
});

const LoadingWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
});

export default function PlanPage({ isPreview = false, expertUrl: propExpertUrl }: PlanPageProps) {
  const { expertSlug, businessSlug } = useParams();
  const isBusinessLink = !!expertSlug && !!businessSlug
  const { state } = useLocation();
  const [loading, setLoading] = useState(true);
  const [expertData, setExpertData] = useState<ExpertData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCalendar, setShowCalendar] = useState(state?.directCalendar || false);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);

  const finalExpertUrl = propExpertUrl || isBusinessLink && `${businessSlug}/${expertSlug}` || expertSlug;
  const navigate = useNavigate();
  const { body: business } = useBusiness()

  useEffect(() => {
    const fetchExpertData = async () => {
      try {
        if (!finalExpertUrl) {
          return
        }
        const publicExpertData = await getPubExpertProfile(finalExpertUrl);
        const {
          first_name,
          last_name,
          preferred_link,
          title,
          profile_image_url,
          description,
          timezone,
          location,
          working_hours,
          blocked_times,
          website,
          instagram_url,
          facebook_url,
          linkedin_url,
        } = publicExpertData
        setExpertData({
          name: `${first_name} ${last_name}` || '',
          preferred_link: preferred_link,
          title: title || 'Trip Expert',
          imageUrl: profile_image_url,
          description: description || '',
          timezone: timezone || '',
          location: location || '',
          businessDomain: website ?? "",
          businessName: business?.name ?? '',
          brandingColor: '#7b5699',
          workingHours: [
            {
              start: '09:00',
              end: '17:00',
              daysOfWeek: [1, 2, 3, 4, 5], // Monday to Friday
            }
          ],
          blockedTimes: [
            {
              start: new Date(2025, 0, 30, 12, 0), // Lunch break
              end: new Date(2025, 0, 30, 13, 0),
            }
          ],
          existingMeetings: [
          ],
          socialLinks: {
            instagram: instagram_url ?? '',
            facebook: facebook_url ?? '',
            linkedin: linkedin_url ?? '',
          },
        });
        setLoading(false);
      } catch (err) {
        setError('Failed to load expert data');
        setLoading(false);
      }
    };

    fetchExpertData();
  }, [finalExpertUrl]);

  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  const handleOpenDialog = () => {
    setShowDialog(true);
  }

  const handleCloseQRCode = () => {
    setShowQRCode(false);
  }

  const handleOpenQRCode = () => {
    setShowQRCode(true);
  }

  const handleCopyLink = (e: any) => {
    if (expertData) {
      navigator.clipboard.writeText(`triplink.vip/${expertData.preferred_link}`);
      setShowCopyToast(true);
    }
  }

  const handleFormEnquiry = () => {
    setShowCalendar(false);
    setShowEnquiryForm(true);
  }

  const handleReturnHome = () => {
    // Clear session storage as we're done with the booking flow
    sessionStorage.removeItem('selectedMeetingDateTime');
    sessionStorage.removeItem('selectedExpert');

    setShowCalendar(false);
    setShowEnquiryForm(false);
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: expertData?.businessName,
          text: expertData?.description,
          url: expertData?.businessDomain
        });
        console.log('Content shared successfully');
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback for browsers that don't support the Web Share API
      alert('Web Share API is not supported in your browser.');
    }
  };

  const handleOpenLink = (url: string) => {
    if (url) {
      window.open(url, '_blank')
    }
  }

  if (loading) {
    return (
      <LoadingWrapper>
        <CircularProgress />
      </LoadingWrapper>
    );
  }

  if (error || !expertData) {
    return (
      <PageContainer>
        <Typography color="error" align="center">
          {error || 'Expert not found'}
        </Typography>
      </PageContainer>
    );
  }

  if (showCalendar && finalExpertUrl) {
    return (
      <PageContainer>
        <CalendarWrapper>
          <BookingCalendar
            expertName={expertData.name}
            profileImage={expertData.imageUrl}
            duration="30"
            timezone={expertData.timezone}
            workingHours={expertData.workingHours}
            blockedTimes={expertData.blockedTimes}
            existingMeetings={expertData.existingMeetings}
            onBack={handleReturnHome}
            onSchedule={(dateTime) => {
              console.log('Scheduled for:', dateTime);
            }}
            isPreview={isPreview}
          />
        </CalendarWrapper>
      </PageContainer>
    );
  }

  if (finalExpertUrl && showEnquiryForm) {
    return (
      <TripQuestionnaire preferredLink={finalExpertUrl} onBack={handleReturnHome} />
    )
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <ExpertCard>
          <CardHeader
            title={expertData.businessName}
            titleTypographyProps={{ fontSize: '15px' }}
            sx={(theme) => ({
              backgroundColor: expertData.brandingColor,
              textAlign: 'center',
              color: 'white',
              width: '100%',
              height: '50px',
              marginBottom: '20px',
              [theme.breakpoints.down("md")]: {
                position: "fixed",
                top: 0,
              }
            })}
          />
          <CardContent
            sx={(theme) => ({
              [theme.breakpoints.down("md")]: {
                mt: "60px",
                mb: "100px"
              }
            })}
          >
            <Box justifyContent="center" display="flex" paddingY={2}>
              <ProfileImage src={`${expertData.imageUrl}?t=${Date.now()}`} />
            </Box>
            <ExpertInfo>
              <Typography variant="h5" component="h1" fontWeight={600}>
                {expertData.name}
              </Typography>
              <Typography variant="h6" component="h3">
                {expertData.title}
              </Typography>
              <Typography variant="h6" component="h3" color="text.secondary" fontSize={16}>
                {expertData.location}
              </Typography>
              <Typography variant="h6" component="h3" color="text.secondary" fontSize={16}>
                Local Time: 21:00 (GMT)
              </Typography>
              <Box display="flex" justifyContent="center" alignItems="center" gap={2} my={2}>
                <CalendarIcon color='grey' />
                <ListIcon color='grey' />
                <SendIcon color='grey' />
                <VideoIcon color='grey' />
                <PhoneIcon color='grey' />
                <IconButton onClick={handleOpenDialog}>
                  <HelpOutline color='primary' />
                </IconButton>
              </Box>
              <Box sx={{ my: 1 }}>
                <ActionButton
                  variant="contained"
                  color="primary"
                  onClick={handleFormEnquiry}
                  disabled={isPreview}
                >
                  Trip Enquiry
                </ActionButton>
                <ActionButton
                  variant="contained"
                  color="primary"
                  onClick={() => setShowCalendar(true)}
                  disabled={isPreview}
                >
                  Schedule Meeting
                </ActionButton>
              </Box>
              <Box textAlign='start'>
                <Typography fontWeight={600} sx={{ padding: "20px 0" }}>
                  About
                </Typography>
                <Typography variant="body1" color="text.primary" sx={{ whiteSpace: "pre-line" }}>
                  {expertData.description}
                </Typography>
              </Box>
              <SocialLinksWrapper>
                {expertData.socialLinks.linkedin && <IconButton sx={{ backgroundColor: '#f7f7f7' }} onClick={() => handleOpenLink(expertData.socialLinks.linkedin)}>
                  <LinkedinIcon color='grey' />
                </IconButton>}
                {expertData.socialLinks.instagram && <IconButton sx={{ backgroundColor: '#f7f7f7' }} onClick={() => handleOpenLink(expertData.socialLinks.instagram)}>
                  <InstagramIcon color='grey' />
                </IconButton>}
                {expertData.socialLinks.facebook && <IconButton sx={{ backgroundColor: '#f7f7f7' }} onClick={() => handleOpenLink(expertData.socialLinks.facebook)}>
                  <FacebookIcon color='grey' />
                </IconButton>}
              </SocialLinksWrapper>
              <Button>
                <InsertLink color='primary' sx={{ mr: 1 }} />
                <Typography textTransform="none">{expertData.businessDomain}</Typography>
              </Button>
            </ExpertInfo>
          </CardContent>
          <BottomSheet sx={{
            backgroundColor: expertData.brandingColor,
          }}>
            <Box>
              <Typography color='white' fontSize={12} marginBottom={1}>triplink.vip/{expertData.preferred_link}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
              <Box textAlign="center">
                <IconButton title='copy' sx={{ backgroundColor: 'white' }} onClick={handleCopyLink}>
                  <Link2Icon />
                </IconButton>
                <Typography color="white" fontSize={12}>Copy</Typography>
              </Box>
              <Box textAlign="center">
                <IconButton sx={{ backgroundColor: 'white' }} onClick={handleShare}>
                  <ShareIcon />
                </IconButton>
                <Typography color="white" fontSize={12}>Share</Typography>
              </Box>
              <Box textAlign="center">
                <IconButton sx={{ backgroundColor: 'white' }} onClick={handleOpenQRCode}>
                  <QRCodeIcon width={24} height={24} />
                </IconButton>
                <Typography color="white" fontSize={12}>QR Code</Typography>
              </Box>
            </Box>
          </BottomSheet>
        </ExpertCard>
      </ContentWrapper>

      <Dialog onClose={handleCloseDialog} open={showDialog}>
        <DialogTitle>
          <IconButton
            color="inherit"
            onClick={handleCloseDialog}
            aria-label="close"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography fontWeight={600}>LET'S CONNECT</Typography>
          <Typography mb={5}>Provide some high level information on the Trip you would like advice or pricing on. I can then do background research prior to connecting and discussing together.</Typography>
          <Typography fontWeight={600}>1. Trip Enquiry</Typography>
          <Typography mb={3}>I will contact you by phone or email some time in the future after reviewing your requirements.</Typography>
          <Typography fontWeight={600}>2. Schedule a Meeting</Typography>
          <Typography>Set up an online voice or video call time now that works for you based on viewing my availability in a calendar. You will be emailed the call link to connect with and instructions.</Typography>
        </DialogContent>
      </Dialog>
      <Dialog onClose={handleCloseQRCode} open={showQRCode}>
        <DialogContent>
          <Box display="flex" justifyContent="center" sx={{ backgroundColor: 'white' }} padding={10}>
            <QRCodeSVG value={expertData.businessDomain} size={256} />
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={3000}
        onClose={() => setShowCopyToast(false)}
        open={showCopyToast}
        message="Copied to the clipboard."
      />
    </PageContainer>
  );
}
